import React from 'react';
import { TrebbleTitle } from './TrebbleTypography';

/**
 * Shared modal/popup header with a title (font-weight 900) and optional
 * subtitle. Keeps title position, size and theme handling consistent across
 * all Trebble popups so every modal looks the same in both light and dark
 * themes.
 *
 * Usage:
 *   <ModalHeader title="Change Plan" subtitle="Choose the plan..." />
 *
 * Inside a TrebbleModal's `title` prop, or directly at the top of any popup
 * content container.
 */
interface ModalHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /**
   * When true the title uses the editor theme colors (dark surfaces).
   * Defaults to false — the title uses the default `text-gray-900
   * dark:text-gray-100` pair so it adapts to app-wide light/dark theme
   * automatically.
   */
  useEditorTheme?: boolean;
  /** Extra spacing below the header block. Defaults to 20px. */
  marginBottom?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  subtitle,
  useEditorTheme = false,
  marginBottom = 20,
  className,
  style,
}) => {
  return (
    <div
      className={className}
      style={{ marginBottom, ...style }}
    >
      <TrebbleTitle
        level={4}
        className="mb-0"
        disableDefaultColors={useEditorTheme}
        style={{ paddingTop: 0, marginTop: 0, marginBottom: subtitle ? 4 : 0 }}
      >
        {title}
      </TrebbleTitle>
      {subtitle && (
        <div
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-muted)',
            lineHeight: 1.4,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
};

export default ModalHeader;
export { ModalHeader };
